// FilePreviewList.jsx
// This component is used to display a preview of images in the file viewer.
// It supports lazy loading of images and allows for editing additional names and comments.
// It can also be used in an edit mode where users can modify file information.

// Copyright HS Analysis GmbH, 2022
// Author: Jakob Burghardt, Viktor Eberhardt

// Framework imports
import React from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";

// MUI imports
import { makeStyles } from "@mui/styles";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import { Tooltip } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RendererPresence from "../../common/services/RendererPresence";

// HSA Imports
import Backend from "../../common/utils/Backend";
import SceneImagePreview from "./SceneImagePreview";
import { FileViewerContext, SortByType } from "../FileViewer";
import { CaseContext } from "../../cases/components/CaseTableDockSideBar";
import SlidePreview from "../../cases/components/SlidePreview";

const useStyles = makeStyles((theme) => ({
  root: {
    minWidth: 0,
    background: "#fff",
    overflow: "hidden",
    height: "100%",
    display: "grid",
    gridTemplateRows: "auto auto 1fr",
  },
  breadcrumb: {
    padding: "6px 8px",
    borderBottom: "1px solid rgba(0,0,0,0.1)",
    background: "#fff",
  },
  breadcrumbRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    alignItems: "center",
    lineHeight: 1.3,
    fontSize: 13,
  },
  crumb: {
    color: "#444",
    textDecoration: "none",
    wordBreak: "break-word",
    overflowWrap: "anywhere",
  },
  crumbCurrent: {
    color: "#444",
    fontWeight: 600,
    wordBreak: "break-word",
    overflowWrap: "anywhere",
  },
  filterInputContainer: {
    margin: 6,
  },
  imgBox: {
    overflow: "auto",
    height: "100%",
    gridRow: 3,
  },
  itemContainer: {
    width: "100%",
    display: "grid",
    gridTemplateColumns: "auto 1fr",
    textAlign: "center",
    gap: 10,
    paddingTop: 10,
  },
  inputContainer: {
    display: "grid",
    width: "100%",
    height: "100%",
    gridTemplateRows: "auto 1fr",
  },
  imgContainer: {
    margin: 5,
    textAlign: "center",
    alignItems: "center",
    cursor: "pointer",
    transition: "all 0.2s ease-in-out",
    "> div >div": {
      border: "3px solid " + theme.palette.primary.inacitve,
    },
    "&:hover >div >div:last-child": {
      backgroundColor: theme.palette.selected.main,
    },
    // Add touch interaction styles
    "@media (hover: none)": {
      "&:active >div >div:last-child": {
        backgroundColor: theme.palette.selected.main,
      },
    },
  },
  imgActive: {
    display: "inline-block",
    cursor: "pointer",
    width: "100%",
    maxHeight: "200px",
    border: `solid 3px ${theme.palette.primary.main}`,
    objectFit: "contain",
  },
  imgSplitScreen: {
    display: "inline-block",
    cursor: "pointer",
    width: "100%",
    maxHeight: "200px",
    border: `dashed 3px ${theme.palette.primary.main}`,
    objectFit: "contain",
  },
  placeholder: {
    height: 200,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  lazyImage: {
    objectFit: "contain",
    width: "100%",
  },
  infoLabel: {
    position: "absolute",
    display: "flex",
    height: "100%",
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "15px",
    color: "#808080",
  },
  ellipsisText: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "block",
    alignItems: "center",
    maxWidth: "150px",
    position: "relative",
    paddingRight: "16px",
  },
  infoIcon: {
    position: "absolute",
    right: 0,
    top: 2,
    marginLeft: 4,
    fontSize: 16,
    color: "#888",
    padding: 0,
    pointerEvents: "auto",
  },
  previewContent: {
    position: "relative",
    width: "100%",
  },
  hasSeenDot: {
    pointerEvents: "auto",
    zIndex: 1000,
    position: "absolute",
    top: 8,
    right: 8,
    width: 10,
    height: 10,
    borderRadius: "50%",
    background: "#4caf50",
  },
  openInNewIcon: {
    position: "absolute",
    top: 4,
    right: 26, // leave room for the green dot at right: 8
    color: "#666",
    fontSize: 18,
    zIndex: 1000,
    pointerEvents: "auto",
    background: "rgba(255,255,255,0.7)",
    borderRadius: 4,
    padding: 2,
  },
  openInNewIconActive: {
    background: theme.palette.primary.main,
    color: "#fff",
  },
}));

/**
 * Preview from the loaded images of the selected Foldertree
 */
const FilePreviewList = (props) => {
  const FV =
    React.useContext(FileViewerContext) || React.useContext(CaseContext);
  // Load persisted sort preferences as defaults when not provided by context
  const getStoredSortBy = React.useCallback(() => {
    try {
      const v = localStorage.getItem("hsaFilePreviewSortBy");
      const allowed = new Set([
        SortByType.Name,
        SortByType.AdditionalName,
        SortByType.Date,
        SortByType.ChangeDate,
        SortByType.Type,
      ]);
      return allowed.has(v) ? v : SortByType.Name;
    } catch {
      return SortByType.Name;
    }
  }, []);
  const getStoredSortReverse = React.useCallback(() => {
    try {
      return localStorage.getItem("hsaFilePreviewSortReverse") === "true";
    } catch {
      return false;
    }
  }, []);
  // Prefer props, fallback to context
  const {
    // Global (potentially empty) scene list is no longer preloaded.
    // We'll lazily fetch per file using pathList.
    sceneDataList,
    setSceneDataList,
    activeFileId,
    activeFilePath = null,
    splitscreenFileIds = [],
    setActiveFileId = null,
    setActiveFilePath = null,
    showAdditionalName = true,
    showComments = true,
    showLabel = true,
    useAdditionalNameInsteadOfFilename = false,
    openInNewWindow = false,
    selCase = null,
    pathList = [],
    // setters possibly used for auto-refreshing the list when active fileId isn't present
    setPathList = null,
    setSelectedFolder = null,
    setCaseId = null,
    caseList = null,
    caseId = null,
    selectedFolder = null,
    // containerWidth = props.containerWidth || 480,
    filePreviewSortBy = getStoredSortBy(),
    filePreviewSortReverse = getStoredSortReverse(),
  } = FV;
  const { editMode } = props;
  const classes = useStyles();
  const history = useHistory();
  const labelHeight = 30;
  const imageContainerRef = React.useRef(null);
  // Map of sceneId -> DOM element for robust lookups even when list is filtered/reordered
  const imageRefs = React.useRef({});
  // Map of path -> placeholder element to observe
  const placeholderRefs = React.useRef({});
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [dialogText, setDialogText] = React.useState("");
  const [dialogTitle, setDialogTitle] = React.useState("");
  const [filterText, setFilterText] = React.useState("");
  // Safe wrappers prevent setState when component is unmounted
  const safeSetDialogOpen = React.useCallback(
    (v) => {
      if (!mountedRef.current) return;
      setDialogOpen(v);
    },
    [setDialogOpen]
  );
  const safeSetDialogText = React.useCallback(
    (v) => {
      if (!mountedRef.current) return;
      setDialogText(v);
    },
    [setDialogText]
  );
  const safeSetDialogTitle = React.useCallback(
    (v) => {
      if (!mountedRef.current) return;
      setDialogTitle(v);
    },
    [setDialogTitle]
  );

  // New: lazily loaded scenes grouped by file path
  const [loadedScenesByPath, setLoadedScenesByPath] = React.useState({});
  const loadingPathsRef = React.useRef(new Set());
  const ioRef = React.useRef(null);
  const visiblePathsRef = React.useRef(new Set());
  const loadTimersRef = React.useRef({});
  const LOAD_DELAY_MS = 300; // Debounce delay for loading visible items
  // Gate loads while the user is actively scrolling
  const scrollIdleRef = React.useRef(true);
  const scrollIdleTimerRef = React.useRef(null);
  const SCROLL_IDLE_MS = 200; // How long after the last scroll to consider idle
  // Scroll-fix utilities to correct position after lazy content renders
  const activeScrollTokenRef = React.useRef(0);
  const scrollFixTimersRef = React.useRef([]);
  // Track mounted state to avoid setState on unmounted component
  const mountedRef = React.useRef(true);
  // Track we already requested switching modes for this activeFileId to avoid loops
  const requestedSwitchToFileViewRef = React.useRef(null);
  const lastActiveFileIdRef = React.useRef(null);
  React.useEffect(() => {
    if (activeFileId !== lastActiveFileIdRef.current) {
      requestedSwitchToFileViewRef.current = null;
      lastActiveFileIdRef.current = activeFileId;
    }
  }, [activeFileId]);

  // Persist sort preferences whenever they change (source-agnostic)
  React.useEffect(() => {
    try {
      localStorage.setItem("hsaFilePreviewSortBy", filePreviewSortBy);
      localStorage.setItem(
        "hsaFilePreviewSortReverse",
        String(filePreviewSortReverse)
      );
    } catch {
      // ignore persistence errors
    }
  }, [filePreviewSortBy, filePreviewSortReverse]);
  React.useEffect(() => {
    return () => {
      mountedRef.current = false;
      // Best-effort cleanup of any pending timers on unmount
      if (scrollIdleTimerRef.current) clearTimeout(scrollIdleTimerRef.current);
      scrollIdleTimerRef.current = null;
      Object.values(loadTimersRef.current).forEach((t) => clearTimeout(t));
      loadTimersRef.current = {};
      // Clear any pending scroll-fix timers
      try {
        (scrollFixTimersRef.current || []).forEach((t) => {
          try {
            if (typeof t === "number") {
              clearTimeout(t);
            } else if (
              t &&
              t.type === "raf" &&
              typeof t.cancelId === "number"
            ) {
              cancelAnimationFrame(t.cancelId);
            } else if (t) {
              clearTimeout(t);
            }
          } catch (_) {
            // ignore
          }
        });
      } catch (_) {
        // ignore
      }
      scrollFixTimersRef.current = [];
      // Disconnect observer if present
      if (ioRef.current) {
        try {
          ioRef.current.disconnect();
        } catch (e) {
          // ignore observer disconnect errors on unmount
        }
      }
    };
  }, []);
  // One-time initial scroll guard
  const didInitialScrollRef = React.useRef(false);
  const normalizePath = React.useCallback(
    (p) => (p ? p.replace(/\\/g, "/") : p),
    []
  );
  const getDirname = React.useCallback(
    (p) => {
      if (!p) return null;
      const norm = normalizePath(p);
      const idx = norm.lastIndexOf("/");
      return idx > 0 ? norm.substring(0, idx) : "";
    },
    [normalizePath]
  );

  // Track seen fileIds across sessions for the green dot
  const [seenIds, setSeenIds] = React.useState(() => {
    try {
      const raw = localStorage.getItem("hsaSeenSceneIds");
      return new Set(raw ? JSON.parse(raw) : []);
    } catch {
      return new Set();
    }
  });

  const persistSeen = React.useCallback((idsSet) => {
    try {
      localStorage.setItem(
        "hsaSeenSceneIds",
        JSON.stringify(Array.from(idsSet))
      );
    } catch (e) {
      // ignore persistence errors
    }
  }, []);

  // Track which fileIds are being rendered in any open window
  const [activeRenderedIds, setActiveRenderedIds] = React.useState(
    () => new Set()
  );
  React.useEffect(() => {
    const unsubscribe = RendererPresence.subscribe((activeSet) => {
      // Ensure we always keep a Set in state for O(1) lookups
      setActiveRenderedIds(new Set(activeSet));
    });
    return () => unsubscribe && unsubscribe();
  }, []);

  // Broadcast a metadata update so other FilePreviewList instances can sync
  const broadcastFileMetaUpdate = React.useCallback((payload) => {
    try {
      window.dispatchEvent(
        new CustomEvent("hsaFileMetaUpdated", { detail: payload })
      );
    } catch (e) {
      // ignore
    }
  }, []);

  // Utility: derive file name from path
  const getFileName = React.useCallback((p) => p.split(/[/\\]/).pop() || p, []);

  // Ensure path is loaded, returning scenes when available
  const ensureLoaded = React.useCallback(
    async (path) => {
      if (!path || loadedScenesByPath[path]) return loadedScenesByPath[path];
      if (loadingPathsRef.current.has(path)) return null;
      loadingPathsRef.current.add(path);
      try {
        const data = await Backend.getFileIdData(path);
        const sceneIds = Array.isArray(data?.sceneIds) ? data.sceneIds : [];
        const fileName = getFileName(path);
        const scenes = sceneIds.map((sc) => ({
          ...sc,
          path,
          sceneCount: sceneIds.length,
          name: fileName,
          hasSeen: seenIds.has(sc.id),
        }));
        if (!mountedRef.current) return null;
        setLoadedScenesByPath((prev) => ({ ...prev, [path]: scenes }));
        return scenes;
      } catch (e) {
        console.error("Failed to load scenes for", path, e);
        return null;
      } finally {
        loadingPathsRef.current.delete(path);
      }
    },
    [loadedScenesByPath, getFileName, seenIds]
  );

  // Removed auto-scroll on lazy load updates to avoid jumping back while user scrolls

  // When active item changes, center it robustly once content is ready and user is idle
  React.useEffect(() => {
    if (!activeFileId) return;
    centerActiveSceneIfNeeded();
  }, [activeFileId]);

  React.useEffect(() => {
    calculatePreviewHeight();
  }, []);

  // When the active file path changes, ensure its scenes are loaded and scroll appropriately.
  // If an activeFileId already exists, scroll to that exact scene; otherwise select and scroll to the first scene.
  React.useEffect(() => {
    if (!activeFilePath) return;
    ensureLoaded(activeFilePath).then((scenes) => {
      if (!mountedRef.current) return;
      const arr = Array.isArray(scenes) ? scenes : [];
      const container = imageContainerRef.current;
      if (!container) return;

      // Try to find the active scene in the loaded list
      let targetScene = null;
      if (activeFileId) {
        targetScene =
          arr.find((s) => String(s.id) === String(activeFileId)) || null;
      }

      // If no active scene found for this path, only fallback to first scene
      // when there is no activeFileId yet. Avoid overriding an existing
      // activeFileId that likely belongs to a different file/path (e.g.,
      // during URL-based back navigation where path and id are updating).
      if (!targetScene && arr.length > 0 && !activeFileId) {
        targetScene = arr[0];
        if (setActiveFileId) setActiveFileId(targetScene.id);
      }

      // Determine the element to scroll to
      const el =
        (targetScene && imageRefs.current?.[targetScene.id]) ||
        placeholderRefs.current?.[activeFilePath];
      if (!el) return;
      if (!isElementFullyVisible(el, container)) {
        // Jump and center when not fully visible (placeholder OK)
        scrollElementIntoCenter(el, container);
      }
      // Follow-up: once lazy content renders, re-center the exact item
      centerActiveSceneIfNeeded();
    });
  }, [activeFilePath, activeFileId]);

  // After lazy scenes load or re-render, try to re-center the active item once
  React.useEffect(() => {
    if (!activeFileId) return;
    centerActiveSceneIfNeeded();
  }, [loadedScenesByPath, activeFileId]);

  // Mark active fileId as seen for green dot and persist
  React.useEffect(() => {
    if (!activeFileId) return;
    if (!seenIds.has(activeFileId)) {
      const next = new Set(seenIds);
      next.add(activeFileId);
      if (mountedRef.current) setSeenIds(next);
      persistSeen(next);
      // Also mark in loaded scenes for immediate UI feedback
      if (mountedRef.current) {
        setLoadedScenesByPath((prev) => {
          const updated = { ...prev };
          Object.keys(updated).forEach((p) => {
            updated[p] = updated[p]?.map((s) =>
              s.id === activeFileId ? { ...s, hasSeen: true } : s
            );
          });
          return updated;
        });
      }
    }
  }, [activeFileId]);

  // Attach scroll listener to detect when user is scrolling vs idle
  React.useEffect(() => {
    const container = imageContainerRef.current;
    if (!container) return;
    const onScroll = () => {
      scrollIdleRef.current = false;
      if (scrollIdleTimerRef.current) clearTimeout(scrollIdleTimerRef.current);
      scrollIdleTimerRef.current = setTimeout(() => {
        scrollIdleRef.current = true;
      }, SCROLL_IDLE_MS);
    };
    container.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      container.removeEventListener("scroll", onScroll);
      if (scrollIdleTimerRef.current) clearTimeout(scrollIdleTimerRef.current);
      scrollIdleTimerRef.current = null;
    };
  }, []);

  // One-time: on initial load, when we have an activeFileId, ensure its path is present and scenes are loaded, then scroll to it
  React.useEffect(() => {
    if (didInitialScrollRef.current) return;
    if (!activeFileId) return;

    let cancelled = false;
    const run = async () => {
      try {
        // Resolve path for active file
        let path = activeFilePath;
        if (!path) {
          try {
            const info = await Backend.getFileInfo(activeFileId);
            path = info?.relativePath || info?.path || null;
          } catch (e) {
            // ignore errors resolving file info on first load
          }
        }
        if (!path) return; // wait for next changes

        // Wait until path is included in the list
        const normTarget = normalizePath(path);
        const isInList = (arr) =>
          (arr || []).some((p) => normalizePath(p.path || p) === normTarget);
        if (!isInList(pathList)) return; // re-run when pathList updates

        // Load scenes for the path
        const scenes = await ensureLoaded(path);
        if (cancelled) return;

        const arr = Array.isArray(scenes) ? scenes : [];
        // Prefer scrolling to the exact active scene if present
        const targetScene =
          arr.find((s) => String(s.id) === String(activeFileId)) ||
          (!activeFileId ? arr[0] : null);
        const container = imageContainerRef.current;
        if (!container) return;

        // Retry until the element ref exists (DOM render can lag behind state)
        const tryScroll = (attempt = 0) => {
          if (cancelled) return;
          const el =
            (targetScene && imageRefs.current?.[targetScene.id]) ||
            placeholderRefs.current?.[path];
          if (el) {
            if (!isElementFullyVisible(el, container)) {
              // Jump and center only if not already visible
              scrollElementIntoCenter(el, container);
            }
            didInitialScrollRef.current = true;
            return;
          }
          if (attempt < 20) {
            setTimeout(() => tryScroll(attempt + 1), 50);
          }
        };
        tryScroll();
      } catch (e) {
        // ignore initial scroll errors
      }
    };
    run();

    return () => {
      cancelled = true;
    };
  }, [activeFileId, activeFilePath, pathList, ensureLoaded, normalizePath]);

  // If a fileId is active but its path isn't part of the current list, refresh the list.
  // - In case view (selCase != null): load the files of the correct case for this file.
  // - In file preview (no case): load files from the same directory as the active file's path.
  React.useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        if (!activeFileId) return;
        // Resolve path for active file
        let path = activeFilePath;
        if (!path) {
          try {
            const info = await Backend.getFileInfo(activeFileId);
            path = info?.relativePath || info?.path || null;
          } catch {
            // ignore
          }
        }
        if (!path) return;

        const target = normalizePath(path);
        const isInList = (arr) =>
          Array.isArray(arr) &&
          arr.some((p) => normalizePath(p.path || p) === target);
        if (isInList(pathList)) return; // already present

        if (selCase) {
          // Find the case that contains this file
          let foundCase = null;
          const cases = caseList?.cases || [];
          for (const c of cases) {
            const files = c?.filePaths || [];
            if (files.some((fp) => normalizePath(fp) === target)) {
              foundCase = c;
              break;
            }
          }

          if (foundCase) {
            // Switch to the correct case; FileViewer will load its files
            // Respect manual case selection via query param (?caseId=...)
            try {
              const params = new URLSearchParams(history.location.search || "");
              if (params.has("caseId")) return;
            } catch (e) {
              // ignore URL parsing errors
            }
            const targetCaseId = foundCase?.data?.Id;
            if (setCaseId && String(caseId) !== String(targetCaseId)) {
              setCaseId(targetCaseId);
            }
            return;
          }
          // Fallback to directory-based load if not associated with any case
          // Switch to file preview mode first so directory selection takes effect,
          // but only request once per activeFileId to avoid loops
          try {
            // If user explicitly selected a case in the query, do not auto-clear to file preview
            const params = new URLSearchParams(history.location.search || "");
            if (!params.has("caseId")) {
              if (
                setCaseId &&
                caseId !== null &&
                requestedSwitchToFileViewRef.current !== activeFileId
              ) {
                requestedSwitchToFileViewRef.current = activeFileId;
                setCaseId(null);
              }
            }
          } catch (e) {
            // ignore URL parsing errors
          }
          return; // wait for next render to handle folder selection
        }

        // File preview mode: load files in the same directory
        const dir = getDirname(path);
        if (!dir) return;
        // Respect user-selected folder: if the user clicked a folder in the tree
        // and it differs from the active file's directory, do not override preview
        if (
          selectedFolder?.user === true &&
          normalizePath(selectedFolder.path) !== normalizePath(dir)
        ) {
          return;
        }
        // Explicitly set both folder selection and path list so reload shows previews
        if (
          setSelectedFolder &&
          (!selectedFolder ||
            normalizePath(selectedFolder.path) !== normalizePath(dir))
        ) {
          setSelectedFolder({ path: dir });
        }
        if (setPathList) {
          try {
            const files = await Backend.walkDir(dir, true);
            if (cancelled || !files) return;
            const filePaths = files.filter((f) => f.type === "file");
            setPathList(filePaths);
          } catch {
            // ignore
          }
        }
        return;
      } catch (e) {
        // no-op on errors
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [
    activeFileId,
    activeFilePath,
    pathList,
    selCase,
    caseList,
    setCaseId,
    setPathList,
    setSelectedFolder,
    normalizePath,
    getDirname,
  ]);

  // Keep activeFileId loosely in sync with URL: only clear when leaving file_view route
  // Avoid clearing activeFilePath here to prevent preview reset flicker; selection is
  // cleared explicitly when the user changes the selected folder (see effect below).
  React.useEffect(() => {
    if (!setActiveFileId && !setActiveFilePath) return;
    const syncFromLocation = (loc) => {
      try {
        const pathname =
          (loc && loc.pathname) || history.location.pathname || "";
        const search = (loc && loc.search) || history.location.search || "";
        const params = new URLSearchParams(search);
        const urlFileId = params.get("fileId");
        const onFileView =
          typeof pathname === "string" && pathname.includes("file_view");
        // Only clear selection when we navigate away from file_view and there's no fileId in URL
        if (!urlFileId && !onFileView) {
          if (setActiveFileId) setActiveFileId(null);
          // Do NOT clear activeFilePath here; keep it until folder changes to avoid resets
        }
      } catch {
        // ignore
      }
    };
    // Initial sync on mount
    syncFromLocation(history.location);
    // Listen for navigation events (Back/Forward/Push)
    const unlisten = history.listen((location /*, action*/) => {
      syncFromLocation(location);
    });
    return () => {
      if (typeof unlisten === "function") unlisten();
    };
  }, [history, setActiveFileId, setActiveFilePath]);

  // Clear active selection when user changes the selected folder (file preview mode only)
  React.useEffect(() => {
    if (selCase) return; // only applies in file preview mode
    if (!setActiveFileId && !setActiveFilePath) return;
    // Only act on explicit user folder changes
    if (selectedFolder?.user === true) {
      if (setActiveFileId) setActiveFileId(null);
      if (setActiveFilePath) setActiveFilePath(null);
    }
  }, [selCase, selectedFolder?.path]);

  // Persist and restore preview scrollTop per directory (file preview mode only)
  const PREVIEW_SCROLL_PREFIX = "hsaPreviewScroll:";
  const lastRestoredDirRef = React.useRef(null);
  const getCurrentDirForScroll = React.useCallback(() => {
    if (selCase) return null; // only for file preview mode
    const s = selectedFolder?.path;
    if (s) return normalizePath(s);
    if (activeFilePath) return normalizePath(getDirname(activeFilePath));
    // fallback: derive from first path in list
    const p =
      Array.isArray(pathList) && pathList.length > 0
        ? pathList[0].path || pathList[0]
        : null;
    if (!p) return null;
    return normalizePath(getDirname(p));
  }, [
    selCase,
    selectedFolder,
    activeFilePath,
    pathList,
    normalizePath,
    getDirname,
  ]);

  // Save scrollTop on scroll
  React.useEffect(() => {
    const container = imageContainerRef.current;
    if (!container) return;
    const onScroll = () => {
      const dir = getCurrentDirForScroll();
      if (!dir) return;
      try {
        localStorage.setItem(
          PREVIEW_SCROLL_PREFIX + dir,
          String(Math.max(0, Math.floor(container.scrollTop)))
        );
      } catch {
        // ignore
      }
    };
    container.addEventListener("scroll", onScroll, { passive: true });
    return () => container.removeEventListener("scroll", onScroll);
  }, [getCurrentDirForScroll]);

  // Restore scrollTop once when a directory's list becomes available
  React.useEffect(() => {
    const container = imageContainerRef.current;
    if (!container) return;
    const dir = getCurrentDirForScroll();
    if (!dir) return;
    if (lastRestoredDirRef.current === dir) return;
    // Only restore when we actually have a list
    if (!Array.isArray(pathList) || pathList.length === 0) return;
    const raw = localStorage.getItem(PREVIEW_SCROLL_PREFIX + dir);
    const offset = raw ? parseInt(raw, 10) : 0;
    if (Number.isFinite(offset) && offset > 0) {
      try {
        container.scrollTop = offset;
      } catch {
        // ignore
      }
    }
    lastRestoredDirRef.current = dir;
  }, [pathList, getCurrentDirForScroll]);

  // IntersectionObserver setup for lazy loading placeholders with debounce and scroll-idle gating
  React.useEffect(() => {
    const container = imageContainerRef.current;
    if (!container) return;
    if (ioRef.current) {
      // Clean previous observer
      ioRef.current.disconnect();
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const el = entry.target;
          const path = el.getAttribute("data-path");
          if (!path) return;
          if (entry.isIntersecting) {
            // Mark as visible and schedule a debounced load
            visiblePathsRef.current.add(path);
            if (!loadTimersRef.current[path]) {
              const attemptLoad = async () => {
                // Only load if still visible and scrolling is idle
                if (!visiblePathsRef.current.has(path)) {
                  // No longer visible, cancel
                  if (loadTimersRef.current[path]) {
                    clearTimeout(loadTimersRef.current[path]);
                    delete loadTimersRef.current[path];
                  }
                  return;
                }
                if (!scrollIdleRef.current) {
                  // Not idle yet, try again shortly
                  loadTimersRef.current[path] = setTimeout(attemptLoad, 150);
                  return;
                }
                await ensureLoaded(path);
                // After loading, no need to keep observing this placeholder
                const node = placeholderRefs.current?.[path];
                if (node) io.unobserve(node);
                if (loadTimersRef.current[path]) {
                  clearTimeout(loadTimersRef.current[path]);
                  delete loadTimersRef.current[path];
                }
              };
              loadTimersRef.current[path] = setTimeout(
                attemptLoad,
                LOAD_DELAY_MS
              );
            }
          } else {
            // No longer visible — cancel any pending load
            visiblePathsRef.current.delete(path);
            const t = loadTimersRef.current[path];
            if (t) {
              clearTimeout(t);
              delete loadTimersRef.current[path];
            }
          }
        });
      },
      { root: container, rootMargin: "0px 0px", threshold: 0.1 }
    );
    ioRef.current = io;

    // Observe current placeholders
    Object.entries(placeholderRefs.current).forEach(([, el]) => {
      if (el) io.observe(el);
    });
    return () => {
      // Cleanup timers and observer
      Object.values(loadTimersRef.current).forEach((t) => clearTimeout(t));
      loadTimersRef.current = {};
      visiblePathsRef.current.clear();
      io.disconnect();
    };
  }, [filterText, pathList, loadedScenesByPath]);

  /**
   * Handles the error event when an image fails to load for a given file.
   * Updates the `missingThumbnails` state to mark the file's thumbnail as missing.
   *
   * @param {Object} file - The file object whose image failed to load.
   * @param {string|number} file.id - The unique identifier of the file.
   */
  const handleImageError = () => {};

  /**
   * Determines if the content of a given HTML element is truncated horizontally.
   *
   * @param {HTMLElement|null} el - The HTML element to check for truncation.
   * @returns {boolean} True if the element's scroll width exceeds its client width, indicating truncation; otherwise, false.
   */
  const isTruncated = (el) => {
    if (!el) return false;
    return el.scrollWidth > el.clientWidth;
  };

  /**
   * Returns true if the element is fully visible within the scroll container's viewport.
   */
  const isElementFullyVisible = (el, container) => {
    if (!el || !container) return true;
    const elRect = el.getBoundingClientRect();
    const cRect = container.getBoundingClientRect();
    return elRect.top >= cRect.top && elRect.bottom <= cRect.bottom;
  };

  // Instantly scroll the container so the element is vertically centered.
  // Falls back to scrollIntoView with block:"center" if manual calc fails.
  const scrollElementIntoCenter = React.useCallback((el, container) => {
    if (!el || !container) return;
    try {
      const elRect = el.getBoundingClientRect();
      const cRect = container.getBoundingClientRect();
      const currentScroll = container.scrollTop;
      const elTop = elRect.top - cRect.top + currentScroll;
      const targetScroll = Math.max(
        0,
        Math.floor(elTop - container.clientHeight / 2 + el.clientHeight / 2)
      );
      container.scrollTop = targetScroll; // jump (no smooth)
    } catch (err) {
      try {
        el.scrollIntoView({
          behavior: "auto",
          block: "center",
          inline: "nearest",
        });
      } catch (_) {
        // ignore
      }
    }
  }, []);

  // Robustly center the active item. Retries until the real element is rendered
  // (after lazy loading) and the user is idle, then recenters again after a
  // couple animation frames to account for size changes.
  const centerActiveSceneIfNeeded = React.useCallback(() => {
    if (!activeFileId) return;
    const token = ++activeScrollTokenRef.current;

    const attempt = (remaining = 12) => {
      if (!mountedRef.current) return;
      // Cancel if a new activation superseded this attempt
      if (activeScrollTokenRef.current !== token) return;

      const container = imageContainerRef.current;
      if (!container) return;

      const el = imageRefs.current?.[activeFileId];
      if (!el) {
        if (remaining <= 0) return;
        const t = setTimeout(() => attempt(remaining - 1), 100);
        scrollFixTimersRef.current.push(t);
        return;
      }

      const doCenter = () => {
        if (!mountedRef.current) return;
        if (activeScrollTokenRef.current !== token) return;
        if (!scrollIdleRef.current) {
          if (remaining <= 0) return;
          const t = setTimeout(() => attempt(remaining - 1), SCROLL_IDLE_MS);
          scrollFixTimersRef.current.push(t);
          return;
        }
        if (!isElementFullyVisible(el, container)) {
          scrollElementIntoCenter(el, container);
        }
        // One or two extra passes on next frames to compensate for image reflow
        try {
          const raf1 = requestAnimationFrame(() => {
            if (!mountedRef.current) return;
            if (activeScrollTokenRef.current !== token) return;
            if (
              scrollIdleRef.current &&
              !isElementFullyVisible(el, container)
            ) {
              scrollElementIntoCenter(el, container);
            }
            const raf2 = requestAnimationFrame(() => {
              if (!mountedRef.current) return;
              if (activeScrollTokenRef.current !== token) return;
              if (
                scrollIdleRef.current &&
                !isElementFullyVisible(el, container)
              ) {
                scrollElementIntoCenter(el, container);
              }
            });
            // Store raf2 as a tagged entry for cleanup symmetry
            scrollFixTimersRef.current.push({ cancelId: raf2, type: "raf" });
          });
          scrollFixTimersRef.current.push({ cancelId: raf1, type: "raf" });
        } catch (_) {
          // ignore
        }
      };
      doCenter();
    };
    attempt();
  }, [activeFileId]);

  // (auto-scroll helper removed to prevent jump-backs during scroll)

  /**
   * Calculates and updates the height property for each item in the sceneDataList array.
   * For each image reference in imageRefs, if the element exists and has a positive clientHeight,
   * sets the corresponding sceneDataList item's height to the sum of the image's height and labelHeight.
   */
  const calculatePreviewHeight = () => {
    for (let i = 0; i < sceneDataList.length; i++) {
      const scene = sceneDataList[i];
      const element1 = imageRefs.current?.[scene.id];
      if (element1) {
        const elemHeight = element1.clientHeight;
        if (elemHeight > 0) {
          sceneDataList[i].height = elemHeight + labelHeight;
        }
      }
    }
  };

  /**
   * Saves an additional name for a given scene if it has changed.
   * @param {Rect.ChangeEvent<HTMLInputElement} e - The change event from the input field.
   * @param {Object} scene - The scene object containing the additional name and id.
   * @param {string|number} scene.id - The unique identifier for the scene.
   * @param {string} [scene.additionalName] - The current additional name associated with
   */
  const saveAdditionalName = (e, scene) => {
    if (e.target.value === scene.additionalName) return;

    Backend.saveFileIdAdditionalName(scene.id, e.target.value)
      .then(() => {
        window.showSuccessSnackbar("Display name saved");
        setSceneDataList((prev) => {
          return prev.map((s) => {
            if (s.id === scene.id) {
              return { ...s, additionalName: e.target.value };
            }
            return s;
          });
        });
        // Also update lazy-loaded scenes map
        if (!mountedRef.current) return; // component unmounted, skip local state update
        setLoadedScenesByPath((prev) => {
          const path = scene.path;
          if (!prev[path]) return prev;
          return {
            ...prev,
            [path]: prev[path].map((s) =>
              s.id === scene.id ? { ...s, additionalName: e.target.value } : s
            ),
          };
        });
        // Notify other components rendering FilePreviewList
        broadcastFileMetaUpdate({
          id: scene.id,
          additionalName: e.target.value,
        });
      })
      .catch((error) => {
        window.openErrorDialog(
          "Error while saving display name:",
          error.message
        );
      });
  };

  /**
   * Saves a comment for a given scene if it has changed.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e - The change event from the input field.
   * @param {Object} scene - The scene object containing the comment and id.
   * @param {string|number} scene.id - The unique identifier for the scene.
   * @param {string} [scene.comment] - The current comment associated with the scene.
   */
  const saveComment = (e, scene) => {
    if (e.target.value === scene.comment) return;

    Backend.saveFileIdComment(scene.id, e.target.value)
      .then(() => {
        window.showSuccessSnackbar("Display name saved");

        setSceneDataList((prev) => {
          return prev.map((s) => {
            if (s.id === scene.id) {
              return { ...s, comment: e.target.value };
            }
            return s;
          });
        });
        // Also update lazy-loaded scenes map
        if (!mountedRef.current) return; // component unmounted, skip local state update
        setLoadedScenesByPath((prev) => {
          const path = scene.path;
          if (!prev[path]) return prev;
          return {
            ...prev,
            [path]: prev[path].map((s) =>
              s.id === scene.id ? { ...s, comment: e.target.value } : s
            ),
          };
        });
        // Notify other components rendering FilePreviewList
        broadcastFileMetaUpdate({ id: scene.id, comment: e.target.value });
      })
      .catch((error) => {
        window.openErrorDialog("Error while saving comment:", error.message);
      });
  };

  // Listen for metadata updates from other FilePreviewList instances and apply locally
  React.useEffect(() => {
    const onMetaUpdate = (ev) => {
      const { id, additionalName, comment } = ev.detail || {};
      if (!id) return;
      if (mountedRef.current) {
        setLoadedScenesByPath((prev) => {
          let changed = false;
          const next = { ...prev };
          for (const p of Object.keys(prev)) {
            const list = prev[p];
            if (!Array.isArray(list)) continue;
            const updated = list.map((s) => {
              if (s.id !== id) return s;
              const upd = {
                ...s,
                ...(additionalName !== undefined ? { additionalName } : null),
                ...(comment !== undefined ? { comment } : null),
              };
              if (upd !== s) changed = true;
              return upd;
            });
            if (updated !== list) next[p] = updated;
          }
          return changed ? next : prev;
        });
        if (setSceneDataList) {
          setSceneDataList((prev) =>
            prev.map((s) =>
              s.id === id
                ? {
                    ...s,
                    ...(additionalName !== undefined
                      ? { additionalName }
                      : null),
                    ...(comment !== undefined ? { comment } : null),
                  }
                : s
            )
          );
        }
      }
    };
    window.addEventListener("hsaFileMetaUpdated", onMetaUpdate);
    return () => window.removeEventListener("hsaFileMetaUpdated", onMetaUpdate);
  }, [setSceneDataList]);

  // Filter file paths by name (case-insensitive)
  const filteredPaths = React.useMemo(() => {
    const files = Array.isArray(pathList) ? pathList : [];
    const names = files.map((f) => f.path);
    if (!filterText) return names;
    const ft = filterText.toLowerCase();
    return names.filter((p) => getFileName(p).toLowerCase().includes(ft));
  }, [pathList, filterText, getFileName]);

  // Build a quick lookup from path -> file metadata (creationTime, etc.)
  const pathMetaMap = React.useMemo(() => {
    const m = new Map();
    const files = Array.isArray(pathList) ? pathList : [];
    for (const f of files) {
      const key = typeof f === "string" ? f : f?.path;
      if (!key) continue;
      m.set(key, f);
    }
    return m;
  }, [pathList]);

  // Compute the ordered list of paths according to current sort settings
  const computedOrderedPaths = React.useMemo(() => {
    // Helpers
    const extractLeadingNumber = (s) => {
      const m = /^\s*(\d+)/.exec((s || "").toString());
      return m ? parseInt(m[1], 10) : null;
    };
    const cmpStr = (a, b) => a.localeCompare(b);

    const toWithKey = (arr, keyFn) =>
      arr.map((p) => ({ path: p, key: keyFn(p) }));

    let ordered = filteredPaths;
    if (filePreviewSortBy === SortByType.AdditionalName) {
      const withKeys = toWithKey(filteredPaths, (p) => {
        const firstScene = loadedScenesByPath[p]?.[0];
        return (firstScene?.additionalName || getFileName(p) || "")
          .toString()
          .toLowerCase();
      });
      withKeys.sort((a, b) => {
        const aNum = extractLeadingNumber(a.key);
        const bNum = extractLeadingNumber(b.key);
        if (aNum !== null && bNum !== null && aNum !== bNum) return aNum - bNum;
        if (a.key !== b.key) return cmpStr(a.key, b.key);
        const an = getFileName(a.path).toLowerCase();
        const bn = getFileName(b.path).toLowerCase();
        if (an !== bn) return cmpStr(an, bn);
        return cmpStr(a.path, b.path);
      });
      if (filePreviewSortReverse) withKeys.reverse();
      ordered = withKeys.map((x) => x.path);
    } else if (filePreviewSortBy === SortByType.Name) {
      const withKeys = toWithKey(filteredPaths, (p) =>
        getFileName(p).toLowerCase()
      );
      withKeys.sort((a, b) => {
        const aNum = extractLeadingNumber(a.key);
        const bNum = extractLeadingNumber(b.key);
        if (aNum !== null && bNum !== null && aNum !== bNum) return aNum - bNum;
        if (a.key !== b.key) return cmpStr(a.key, b.key);
        return cmpStr(a.path, b.path);
      });
      if (filePreviewSortReverse) withKeys.reverse();
      ordered = withKeys.map((x) => x.path);
    } else if (filePreviewSortBy === SortByType.Type) {
      const withKeys = toWithKey(filteredPaths, (p) => {
        const parts = p.split(".");
        return parts.length > 1 ? parts.pop().toLowerCase() : "";
      });
      withKeys.sort((a, b) => {
        if (a.key !== b.key) return cmpStr(a.key, b.key);
        const an = getFileName(a.path).toLowerCase();
        const bn = getFileName(b.path).toLowerCase();
        if (an !== bn) return cmpStr(an, bn);
        return cmpStr(a.path, b.path);
      });
      if (filePreviewSortReverse) withKeys.reverse();
      ordered = withKeys.map((x) => x.path);
    } else if (
      filePreviewSortBy === SortByType.Date ||
      filePreviewSortBy === SortByType.ChangeDate
    ) {
      // Backend provides creationTime in format yyyy/MM/dd HH:mm:ss which is lexicographically sortable.
      const withKeys = toWithKey(filteredPaths, (p) => {
        const meta = pathMetaMap.get(p);
        const ct = (meta && meta.creationTime) || "";
        return ct;
      });
      withKeys.sort((a, b) => {
        if (a.key !== b.key) return cmpStr(a.key, b.key);
        // Tie-breaker by name
        const an = getFileName(a.path).toLowerCase();
        const bn = getFileName(b.path).toLowerCase();
        if (an !== bn) return cmpStr(an, bn);
        return cmpStr(a.path, b.path);
      });
      if (filePreviewSortReverse) withKeys.reverse();
      ordered = withKeys.map((x) => x.path);
    }
    return ordered;
  }, [
    filteredPaths,
    loadedScenesByPath,
    getFileName,
    filePreviewSortBy,
    filePreviewSortReverse,
    pathMetaMap,
  ]);

  // Frozen ordering while in edit mode to avoid jumping when metadata changes
  const [frozenOrderedPaths, setFrozenOrderedPaths] = React.useState([]);

  // Keep frozen order updated only when the number of preview files changes, or when toggling edit mode
  React.useEffect(() => {
    if (!editMode) {
      if (frozenOrderedPaths.length > 0) setFrozenOrderedPaths([]);
      return;
    }
    // Initialize or refresh the frozen order if count changed
    if (frozenOrderedPaths.length !== filteredPaths.length) {
      setFrozenOrderedPaths(computedOrderedPaths);
    }
  }, [editMode, filteredPaths.length, computedOrderedPaths]);

  // Reconcile helper: keep the frozen relative ordering, but reflect the current visible set
  const getOrderedPathsForRender = React.useCallback(() => {
    if (!editMode) return computedOrderedPaths;
    // Start with frozen order, remove paths no longer visible
    const visibleSet = new Set(filteredPaths);
    const base = frozenOrderedPaths.filter((p) => visibleSet.has(p));
    // Append any newly visible paths in the order of computedOrderedPaths
    const inBase = new Set(base);
    computedOrderedPaths.forEach((p) => {
      if (visibleSet.has(p) && !inBase.has(p)) base.push(p);
    });
    return base;
  }, [editMode, computedOrderedPaths, filteredPaths, frozenOrderedPaths]);

  // Flatten scenes for rendering: for each path, if loaded use all scenes, else a placeholder entry
  const renderItems = React.useMemo(() => {
    const items = [];
    // Determine ordered paths, honoring frozen order when in edit mode
    const orderedPaths = getOrderedPathsForRender();

    for (const path of orderedPaths) {
      const scenes = loadedScenesByPath[path];
      if (Array.isArray(scenes) && scenes.length > 0) {
        // When not in edit mode and sorting by AdditionalName, also order scenes within the same file
        if (!editMode && filePreviewSortBy === SortByType.AdditionalName) {
          const extractLeadingNumber = (s) => {
            const m = /^\s*(\d+)/.exec(s || "");
            return m ? parseInt(m[1], 10) : null;
          };
          const withIdx = scenes.map((s, idx) => {
            const key = (s?.additionalName || s?.name || "")
              .toString()
              .toLowerCase();
            return { s, idx, key };
          });
          withIdx.sort((a, b) => {
            const aNum = extractLeadingNumber(a.key);
            const bNum = extractLeadingNumber(b.key);
            if (aNum !== null && bNum !== null && aNum !== bNum) {
              return aNum - bNum;
            }
            if (a.key !== b.key) return a.key.localeCompare(b.key);
            // Tie-breaker: keep original scene order (scene numbering)
            return a.idx - b.idx;
          });
          const orderedScenes = filePreviewSortReverse
            ? withIdx.reverse().map((x) => x.s)
            : withIdx.map((x) => x.s);
          orderedScenes.forEach((s) => items.push(s));
        } else {
          scenes.forEach((s) => items.push(s));
        }
      } else {
        items.push({
          id: `placeholder:${path}`,
          placeholder: true,
          path,
          name: getFileName(path),
        });
      }
    }
    return items;
  }, [
    getOrderedPathsForRender,
    loadedScenesByPath,
    getFileName,
    filePreviewSortBy,
    filePreviewSortReverse,
    editMode,
  ]);
  return (
    <div className={classes.root}>
      {/* Breadcrumb above filter (hidden in slide preview/case mode) */}
      {!selCase &&
        (() => {
          const baseName = (p) => (p ? p.split(/[\\/]+/).pop() : "");
          const dirOf = (p) => {
            if (!p) return "";
            const i1 = p.lastIndexOf("/");
            const i2 = p.lastIndexOf("\\");
            const i = Math.max(i1, i2);
            return i > 0 ? p.substring(0, i) : "";
          };
          const currentDir = activeFilePath
            ? dirOf(activeFilePath)
            : selectedFolder?.path || "";
          const sep = currentDir.includes("\\") ? "\\" : "/";
          const parts = currentDir
            ? currentDir.split(/[\\/]+/).filter(Boolean)
            : [];
          const crumbs = [];
          let acc = "";
          parts.forEach((part, idx) => {
            acc = acc ? acc + sep + part : part;
            const isLast = idx === parts.length - 1;
            if (!isLast) {
              crumbs.push(
                <span key={`c-${idx}`}>
                  <span className={classes.crumb}>{part}</span>
                  <span
                    className={classes.breadcrumbRow}
                    style={{ gap: 0, display: "inline-flex" }}
                  >
                    <span style={{ color: "#888", padding: "0 4px" }}>/</span>
                  </span>
                </span>
              );
            } else {
              crumbs.push(
                <span key={`c-${idx}`} className={classes.crumbCurrent}>
                  {part || baseName(currentDir)}
                </span>
              );
            }
          });
          return (
            <div className={classes.breadcrumb}>
              <div className={classes.breadcrumbRow}>
                {parts.length > 0 ? (
                  crumbs
                ) : (
                  <span className={classes.crumbCurrent}>
                    {baseName(currentDir) || ""}
                  </span>
                )}
              </div>
            </div>
          );
        })()}
      {/* Filter input: shown only when previews are available */}
      {Array.isArray(pathList) && pathList.length > 0 && (
        <div className={classes.filterInputContainer}>
          <TextField
            variant="outlined"
            size="small"
            fullWidth
            placeholder="Filter by file name"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") setFilterText("");
            }}
          />
        </div>
      )}
      <div
        id="file-preview-scroll-container"
        className={classes.imgBox}
        ref={(el) => (imageContainerRef.current = el)}
      >
        {/* If empty, show message inline (breadcrumb remains visible above) */}
        {(!Array.isArray(pathList) || pathList.length === 0) && (
          <div
            style={{
              height: "100%",
              width: "100%",
              display: "grid",
              gridTemplateRows: "1fr",
              alignItems: "center",
              justifyItems: "center",
              textAlign: "center",
            }}
          >
            <div style={{ color: "#808080", fontWeight: 600 }}>
              No Files or CASES
            </div>
          </div>
        )}
        {renderItems.map((scene) => {
          let fileStatus = "img";
          if (!scene.placeholder && activeFileId === scene.id) {
            fileStatus = "imgActive";
          } else if (
            !scene.placeholder &&
            splitscreenFileIds.includes(scene.id)
          ) {
            fileStatus = "imgSplitScreen";
          }
          return (
            <div
              key={scene.path + scene.id}
              className={
                editMode ? classes.itemContainer : classes.imgContainer
              } // Use a container class for grouping
              ref={(el) => {
                if (scene.placeholder) {
                  // Track placeholder for IO observation
                  if (el) {
                    placeholderRefs.current[scene.path] = el;
                    el.setAttribute("data-path", scene.path);
                    ioRef.current?.observe(el);
                  } else if (placeholderRefs.current[scene.path]) {
                    ioRef.current?.unobserve(
                      placeholderRefs.current[scene.path]
                    );
                    delete placeholderRefs.current[scene.path];
                  }
                } else {
                  if (el) {
                    imageRefs.current[scene.id] = el;
                  } else if (imageRefs.current[scene.id]) {
                    delete imageRefs.current[scene.id];
                  }
                }
              }}
              // Allow dragging into the renderer for splitscreen even when a case is active
              // Previously this was disabled with `!selCase`, which blocked dragging SlidePreview tiles.
              // We only block dragging in edit mode and for placeholders.
              draggable={!editMode && !scene.placeholder}
              onDragStart={(e) => {
                if (!scene.placeholder) {
                  e.dataTransfer.setData("fileId", scene.id);
                }
              }}
              onClick={(e) => {
                if (editMode) return;
                if (scene.placeholder) {
                  // Trigger load and select first scene once available
                  ensureLoaded(scene.path).then((scenes) => {
                    if (!mountedRef.current) return;
                    if (Array.isArray(scenes) && scenes.length > 0) {
                      try {
                        const params = new URLSearchParams(
                          history.location.search || ""
                        );
                        params.set("fileId", scenes[0].id);
                        if (selCase && selCase.Id) {
                          params.set("caseId", String(selCase.Id));
                        } else {
                          params.delete("caseId");
                        }
                        const basePath = `/file_view`;
                        // Previously used to decide between replace/push; always push now
                        const search = params.toString();
                        const normalUrl = `${basePath}${
                          search ? `?${search}` : ""
                        }`;
                        // Popup URL includes UI flags
                        const popupUrl = `${basePath}${
                          search
                            ? `?${search}&sidebar=hidden&navbar=hidden`
                            : `?sidebar=hidden&navbar=hidden`
                        }`;
                        if (openInNewWindow) {
                          // prevent any further handling in the opener tab
                          if (e && typeof e.preventDefault === "function")
                            e.preventDefault();
                          if (e && typeof e.stopPropagation === "function")
                            e.stopPropagation();
                          // Open as a real popup window with minimal chrome; modern browsers may still enforce some UI
                          // Compute popup geometry from top-left and cascade in X direction
                          // Prefer persisted geometry (from last popup) if available
                          let availW =
                            window.screen.availWidth ||
                            window.innerWidth ||
                            1280;
                          let availH =
                            window.screen.availHeight ||
                            window.innerHeight ||
                            800;
                          let baseLeft = (window.screen.availLeft || 0) + 10;
                          let baseTop = (window.screen.availTop || 0) + 10;
                          try {
                            const sLeft = Number(
                              localStorage.getItem("hsaOtherScreenLeft")
                            );
                            const sTop = Number(
                              localStorage.getItem("hsaOtherScreenTop")
                            );
                            const sW = Number(
                              localStorage.getItem("hsaOtherScreenWidth")
                            );
                            const sH = Number(
                              localStorage.getItem("hsaOtherScreenHeight")
                            );
                            if (
                              Number.isFinite(sLeft) &&
                              Number.isFinite(sTop) &&
                              Number.isFinite(sW) &&
                              Number.isFinite(sH) &&
                              sW > 200 &&
                              sH > 200
                            ) {
                              // using persisted geometry for popup
                              availW = sW;
                              availH = sH;
                              baseLeft = sLeft + 10;
                              baseTop = sTop + 10;
                            } else {
                              // no valid persisted geometry, using current screen
                            }
                          } catch (err) {
                            // failed reading persisted geometry
                          }
                          const width = Math.min(
                            1000,
                            Math.max(960, Math.floor(availW * 0.85))
                          );
                          const height = Math.max(
                            720,
                            Math.floor(availH * 0.85)
                          );
                          // X-only cascade with wrap after 10
                          let idx = 0;
                          try {
                            idx =
                              parseInt(
                                localStorage.getItem("hsaPopupCascadeIndexX") ||
                                  "0",
                                10
                              ) || 0;
                          } catch (err) {
                            /* ignore */
                          }
                          const offset = 36 * (idx % 10);
                          const left = Math.max(
                            baseLeft,
                            Math.min(
                              baseLeft + offset,
                              baseLeft + Math.max(0, availW - width)
                            )
                          );
                          const top = baseTop; // no Y cascading
                          try {
                            localStorage.setItem(
                              "hsaPopupCascadeIndexX",
                              String((idx + 1) % 10)
                            );
                          } catch (err) {
                            /* ignore */
                          }
                          const features = [
                            "noopener",
                            "toolbar=no",
                            "menubar=no",
                            "location=no",
                            "status=no",
                            "scrollbars=yes",
                            "resizable=yes",
                            `width=${width}`,
                            `height=${height}`,
                            `left=${left}`,
                            `top=${top}`,
                          ].join(",");
                          const w = window.open(popupUrl, "_blank", features);
                          if (!w) {
                            // Some browsers may return null even when the popup opens; warn only if opener keeps focus
                            setTimeout(() => {
                              try {
                                if (document.hasFocus && document.hasFocus()) {
                                  if (
                                    typeof window.showWarningSnackbar ===
                                    "function"
                                  ) {
                                    window.showWarningSnackbar(
                                      "Popup was blocked. Please allow popups for HSA-KIT to open in a new window."
                                    );
                                  }
                                }
                              } catch {
                                // ignore focus check failures
                              }
                            }, 200);
                            return;
                          }
                          // Enforce geometry after open in case browser ignored features
                          try {
                            w.focus();
                            w.resizeTo(width, height);
                            w.moveTo(left, top);
                            setTimeout(() => {
                              try {
                                w.resizeTo(width, height);
                                w.moveTo(left, top);
                              } catch (err) {
                                /* ignore */
                              }
                            }, 50);
                          } catch (err) {
                            /* ignore */
                          }
                          // On successful open, do not change state or URL in the opener tab
                          if (w) return;
                        } else {
                          if (mountedRef.current) {
                            // Always push a new history entry so Back returns to cases?caseId=... instead of root
                            history.push(normalUrl);
                          }
                        }
                        // Only update local selection in the current tab when not opening a new window (or when popup blocked)
                        if (setActiveFileId) setActiveFileId(scenes[0].id);
                        if (setActiveFilePath) setActiveFilePath(scene.path);
                      } catch (e) {
                        // ignore URL update errors
                      }
                    }
                  });
                  return;
                }
                // Push current selection into browser history so back/forward navigates between selections
                try {
                  const params = new URLSearchParams(
                    history.location.search || ""
                  );
                  const currentId = params.get("fileId");
                  if (currentId === String(scene.id)) return; // avoid duplicate entries
                  params.set("fileId", scene.id);
                  if (selCase && selCase.Id) {
                    params.set("caseId", String(selCase.Id));
                  } else {
                    params.delete("caseId");
                  }
                  const basePath = `/file_view`;
                  // Previously used to decide between replace/push; always push now
                  const search = params.toString();
                  const normalUrl = `${basePath}${search ? `?${search}` : ""}`;
                  const popupUrl = `${basePath}${
                    search
                      ? `?${search}&sidebar=hidden&navbar=hidden`
                      : `?sidebar=hidden&navbar=hidden`
                  }`;
                  if (openInNewWindow) {
                    if (e && typeof e.preventDefault === "function")
                      e.preventDefault();
                    if (e && typeof e.stopPropagation === "function")
                      e.stopPropagation();
                    // Compute popup geometry from top-left and cascade in X direction
                    // Prefer persisted geometry (from last popup) if available
                    let availW =
                      window.screen.availWidth || window.innerWidth || 1280;
                    let availH =
                      window.screen.availHeight || window.innerHeight || 800;
                    let baseLeft = (window.screen.availLeft || 0) + 10;
                    let baseTop = (window.screen.availTop || 0) + 10;
                    try {
                      const sLeft = Number(
                        localStorage.getItem("hsaOtherScreenLeft")
                      );
                      const sTop = Number(
                        localStorage.getItem("hsaOtherScreenTop")
                      );
                      const sW = Number(
                        localStorage.getItem("hsaOtherScreenWidth")
                      );
                      const sH = Number(
                        localStorage.getItem("hsaOtherScreenHeight")
                      );
                      if (
                        Number.isFinite(sLeft) &&
                        Number.isFinite(sTop) &&
                        Number.isFinite(sW) &&
                        Number.isFinite(sH) &&
                        sW > 200 &&
                        sH > 200
                      ) {
                        // using persisted geometry for popup
                        availW = sW;
                        availH = sH;
                        baseLeft = sLeft + 10;
                        baseTop = sTop + 10;
                      } else {
                        // no valid persisted geometry, using current screen
                      }
                    } catch (err) {
                      // failed reading persisted geometry
                    }
                    const width = Math.min(
                      1000,
                      Math.max(960, Math.floor(availW * 0.85))
                    );
                    const height = Math.max(720, Math.floor(availH * 0.85));
                    let idx = 0;
                    try {
                      idx =
                        parseInt(
                          localStorage.getItem("hsaPopupCascadeIndexX") || "0",
                          10
                        ) || 0;
                    } catch (err) {
                      /* ignore */
                    }
                    const offset = 36 * (idx % 10);
                    const left = Math.max(
                      baseLeft,
                      Math.min(
                        baseLeft + offset,
                        baseLeft + Math.max(0, availW - width)
                      )
                    );
                    const top = baseTop;
                    try {
                      localStorage.setItem(
                        "hsaPopupCascadeIndexX",
                        String((idx + 1) % 10)
                      );
                    } catch (err) {
                      /* ignore */
                    }
                    const features = [
                      "noopener",
                      "toolbar=no",
                      "menubar=no",
                      "location=no",
                      "status=no",
                      "scrollbars=yes",
                      "resizable=yes",
                      `width=${width}`,
                      `height=${height}`,
                      `left=${left}`,
                      `top=${top}`,
                    ].join(",");
                    const w = window.open(popupUrl, "_blank", features);
                    if (!w) {
                      // Some browsers may return null even when the popup opens; warn only if opener keeps focus
                      setTimeout(() => {
                        try {
                          if (document.hasFocus && document.hasFocus()) {
                            if (
                              typeof window.showWarningSnackbar === "function"
                            ) {
                              window.showWarningSnackbar(
                                "Popup was blocked. Please allow popups for HSA-KIT to open in a new window."
                              );
                            }
                          }
                        } catch {
                          // ignore focus check failures
                        }
                      }, 200);
                      return;
                    }
                    // Enforce geometry after open
                    try {
                      w.focus();
                      w.resizeTo(width, height);
                      w.moveTo(left, top);
                      setTimeout(() => {
                        try {
                          w.resizeTo(width, height);
                          w.moveTo(left, top);
                        } catch (err) {
                          /* ignore */
                        }
                      }, 50);
                    } catch (err) {
                      /* ignore */
                    }
                    if (w) return; // don't alter state/URL in the opener tab
                  } else {
                    // Always push when navigating from cases to file_view so Back returns to cases?caseId=...
                    history.push(normalUrl);
                  }
                  // Only update local selection when staying in this tab
                  if (setActiveFileId) {
                    setActiveFileId(scene.id);
                  }
                  if (setActiveFilePath) {
                    setActiveFilePath(scene.path);
                  }
                } catch (err) {
                  // no-op if URLSearchParams fails
                }
              }}
            >
              <div className={classes.previewContent}>
                {openInNewWindow && !scene.placeholder && (
                  <Tooltip
                    disableInteractive
                    placement="top"
                    title={"Will open in new Window"}
                  >
                    <OpenInNewIcon
                      className={
                        activeRenderedIds.has(scene.id)
                          ? `${classes.openInNewIcon} ${classes.openInNewIconActive}`
                          : classes.openInNewIcon
                      }
                    />
                  </Tooltip>
                )}
                {!scene.placeholder &&
                  (scene.hasSeen || seenIds.has(scene.id)) && (
                    <Tooltip
                      disableInteractive
                      placement="top"
                      title={"This file has been seen before!"}
                    >
                      <div className={classes.hasSeenDot}></div>
                    </Tooltip>
                  )}
                {selCase ? (
                  scene.placeholder ? (
                    <div className={classes.placeholder}>
                      <div style={{ color: "#808080" }}>
                        {getFileName(scene.path)}
                      </div>
                    </div>
                  ) : (
                    <SlidePreview
                      showAdditionalName={showAdditionalName}
                      showComments={showComments}
                      showLabel={showLabel}
                      useAdditionalNameInsteadOfFilename={
                        useAdditionalNameInsteadOfFilename
                      }
                      fileStatus={fileStatus}
                      file={scene}
                      containerWidth={500}
                    />
                  )
                ) : scene.placeholder ? (
                  <div className={classes.placeholder}>
                    {/* Minimal placeholder tile until scenes are loaded */}
                    <div style={{ color: "#808080" }}>
                      {getFileName(scene.path)}
                    </div>
                  </div>
                ) : (
                  <SceneImagePreview
                    scene={scene}
                    showAdditionalName={showAdditionalName}
                    showComments={showComments}
                    useAdditionalNameInsteadOfFilename={
                      useAdditionalNameInsteadOfFilename
                    }
                    fileStatus={fileStatus}
                    width={editMode ? "200px" : ""}
                    isTruncated={isTruncated}
                    setDialogTitle={safeSetDialogTitle}
                    setDialogText={safeSetDialogText}
                    setDialogOpen={safeSetDialogOpen}
                    handleImageError={handleImageError}
                  />
                )}
              </div>

              {editMode && !scene.placeholder && (
                <div className={classes.inputContainer}>
                  <TextField
                    key={`an-${scene.id}-${scene.additionalName || ""}`}
                    label="Additional Name"
                    variant="outlined"
                    size="small"
                    fullWidth
                    style={{ marginBottom: 8 }}
                    defaultValue={scene.additionalName || ""}
                    onBlur={(e) => saveAdditionalName(e, scene)}
                  />
                  <TextField
                    key={`c-${scene.id}-${scene.comment || ""}`}
                    label="Comment"
                    variant="outlined"
                    size="small"
                    fullWidth
                    multiline
                    minRows={3}
                    maxRows={8}
                    InputProps={{
                      style: { height: "100%", alignItems: "flex-start" },
                    }}
                    style={{ flex: 1 }}
                    defaultValue={scene.comment || ""}
                    onBlur={(e) => saveComment(e, scene)}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Dialog
        open={dialogOpen}
        onClose={() => safeSetDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent>
          <div style={{ whiteSpace: "pre-wrap" }}>{dialogText}</div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

FilePreviewList.propTypes = {
  // Optional props
  editMode: PropTypes.bool,
};

// defautl props
FilePreviewList.defaultProps = {
  editMode: false,
};

export default FilePreviewList;
